import { useEffect, useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import DiligencePackSelectCard from '@tpct/mission-core/components/DiligencePackSelectCard';
import type { MissionApiConfig } from '../../types/missionPortal';
import {
  fetchMissionDiligencePacks,
  saveMissionDiligencePacks,
  updateMissionSetup,
  type DiligencePackSuggestion,
} from '@tpct/mission-core/utils/missionStartApi';
import { errorToUserFacingMessage } from '../../api/apiErrors';

type MissionCompliancePanelProps = {
  apiConfig: MissionApiConfig;
  missionId: number;
  publishCatalog: boolean;
  readOnly: boolean;
  onPublishCatalogChange: (value: boolean) => void;
  onError: (message: string) => void;
  onUpdated: () => Promise<void>;
};

const MissionCompliancePanel = ({
  apiConfig,
  missionId,
  publishCatalog,
  readOnly,
  onPublishCatalogChange,
  onError,
  onUpdated,
}: MissionCompliancePanelProps) => {
  const [suggestions, setSuggestions] = useState<DiligencePackSuggestion[]>([]);
  const [selectedPackIds, setSelectedPackIds] = useState<number[]>([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [packsSaving, setPacksSaving] = useState(false);
  const [publishSaving, setPublishSaving] = useState(false);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setPacksLoading(true);
      onError('');
      try {
        const data = await fetchMissionDiligencePacks(apiConfig, missionId);
        if (!active) {
          return;
        }
        setSuggestions(data.suggestions);
        setSelectedPackIds(
          data.suggestions
            .filter((pack) => pack.selected && !pack.locked)
            .map((pack) => pack.pack_id),
        );
      } catch (e) {
        if (active) {
          onError(errorToUserFacingMessage(e, __('Impossible de charger les packs de diligences.')));
        }
      } finally {
        if (active) {
          setPacksLoading(false);
        }
      }
    };

    void run();
    return () => {
      active = false;
    };
  }, [apiConfig, missionId, onError]);

  const togglePack = (packId: number) => {
    if (readOnly || packsSaving) {
      return;
    }
    const next = selectedPackIds.includes(packId)
      ? selectedPackIds.filter((id) => id !== packId)
      : [...selectedPackIds, packId];
    setSelectedPackIds(next);
  };

  const savePacks = async () => {
    if (readOnly || packsSaving) {
      return;
    }
    setPacksSaving(true);
    onError('');
    try {
      await saveMissionDiligencePacks(apiConfig, missionId, selectedPackIds);
      await onUpdated();
    } catch (e) {
      onError(errorToUserFacingMessage(e, __("Impossible d\u2019enregistrer les packs de diligences.")));
    } finally {
      setPacksSaving(false);
    }
  };

  const handlePublishChange = async (checked: boolean) => {
    if (readOnly || publishSaving) {
      return;
    }
    setPublishSaving(true);
    onError('');
    try {
      await updateMissionSetup(apiConfig, missionId, { publish_catalog: checked });
      onPublishCatalogChange(checked);
      await onUpdated();
    } catch (e) {
      onError(errorToUserFacingMessage(e, __("Impossible d\u2019enregistrer la pr\u00e9f\u00e9rence de publication.")));
    } finally {
      setPublishSaving(false);
    }
  };

  return (
    <section className="container-style mission-page__card mission-page__card--span-3" aria-labelledby="mission-compliance-heading">
      <h2 id="mission-compliance-heading" className="h4">{__('Contrôle et conformité')}</h2>

      <div className="mission-compliance__field">
        <h3 className="mission-compliance__subtitle">{__('Publier le rapport')}</h3>
        <p className="mission-compliance__help" id="mission-publish-catalog-help">
          {__(
            'En cochant cette case, vous acceptez de publier le rapport sur le site et de le transmettre à des fins statistiques.',
          )}
        </p>
        <div className="checkbox-container">
          <input
            id="mission-publish-catalog"
            type="checkbox"
            checked={publishCatalog}
            disabled={readOnly || publishSaving}
            onChange={(event) => {
              void handlePublishChange(event.target.checked);
            }}
            aria-describedby="mission-publish-catalog-help"
          />
          <label htmlFor="mission-publish-catalog">{__('Publier le rapport')}</label>
        </div>
      </div>

      <div className="mission-compliance__field">
        <h3 className="mission-compliance__subtitle">{__('Pack de diligences')}</h3>
        <p className="mission-compliance__help">
          {__('Sélectionnez les packs de diligences à appliquer à cette mission.')}
        </p>
        {packsLoading ? <p>{__('Chargement des packs...')}</p> : (
          <div className="compliance-tab__packs-grid">
            {suggestions.map((pack) => (
              <DiligencePackSelectCard
                key={pack.pack_id}
                pack={pack}
                selected={pack.locked || selectedPackIds.includes(pack.pack_id)}
                onToggle={() => togglePack(pack.pack_id)}
                disabled={readOnly || pack.locked || packsSaving}
              />
            ))}
          </div>
        )}
        {!readOnly && !packsLoading ? (
          <button type="button" className="btn-secondary" disabled={packsSaving} onClick={() => { void savePacks(); }}>
            {packsSaving ? __('Enregistrement...') : __('Enregistrer les packs')}
          </button>
        ) : null}
      </div>
    </section>
  );
};

export default MissionCompliancePanel;
